/**
 * Deterministisk, kollisionsfri utplacering av tärningar på spelplanen.
 *
 * Bakgrund (2026-08-15): tidigare slumpades positioner med kollisionskontroll
 * och en "sista fallback" som packade alla tärningar i EN rad med jämna steg –
 * även när stegen blev mindre än tärningen. Eftersom 5–6 tärningar à ~104 px
 * aldrig får plats på en rad i en ~440 px bred spelplan triggades fallbacken
 * praktiskt taget alltid, och tärningarna lades ovanpå varandra så att
 * prickarna inte gick att läsa.
 *
 * Den här funktionen räknar i stället ut ett rutnät som får plats, centrerar
 * det i ytan och lägger till en liten slumpmässig förskjutning inom varje cell
 * för "kastad"-känslan. Överlapp kan bara uppstå om ytan är fysiskt för liten
 * för antalet tärningar – och då packas de ändå jämnt i stället för slumpvis.
 */

export interface DiceLayoutOptions {
  /** Antal tärningar att placera. */
  count: number;
  /** Spelplanens inre bredd i px. */
  width: number;
  /** Spelplanens inre höjd i px. */
  height: number;
  /** Tärningens renderade storlek i px. */
  dieSize: number;
  /** Andel (0–1) av cellens lediga utrymme som får användas till slump. */
  jitter?: number;
  /** Injicerbar RNG för deterministiska tester. */
  rng?: () => number;
}

export interface DicePosition { x: number; y: number }

/** Extra utrymme för att tärningen ska kunna snurra utan att nudda nästa. */
const ROLL_SCALE = 1.07;

/**
 * Väljer det antal kolumner som ger störst möjliga cellstorlek och samtidigt
 * håller alla tärningar innanför ytan.
 */
export function chooseGrid(count: number, width: number, height: number, spacing: number): { cols: number; rows: number } {
  if (count <= 1) return { cols: 1, rows: 1 };

  let best: { cols: number; rows: number; score: number } | null = null;
  for (let cols = 1; cols <= count; cols++) {
    const rows = Math.ceil(count / cols);
    const fitsWidth = cols * spacing <= width;
    const fitsHeight = rows * spacing <= height;
    if (!fitsWidth || !fitsHeight) continue;
    // Maximera minsta cellsidan → mest luft runt varje tärning.
    const score = Math.min(width / cols, height / rows);
    if (!best || score > best.score) best = { cols, rows, score };
  }
  if (best) return { cols: best.cols, rows: best.rows };

  // Inget rutnät får plats med full lucka: packa så jämnt som möjligt utifrån
  // ytans proportioner (hellre tätt och ordnat än slumpvis ovanpå varandra).
  const ratio = width / Math.max(1, height);
  const cols = Math.max(1, Math.min(count, Math.round(Math.sqrt(count * ratio))));
  return { cols, rows: Math.ceil(count / cols) };
}

export function computeDicePositions(options: DiceLayoutOptions): DicePosition[] {
  const { count, width, height, dieSize, jitter = 0.35, rng = Math.random } = options;
  if (count <= 0) return [];

  const effective = dieSize * ROLL_SCALE;
  const margin = Math.max(8, dieSize * 0.1);
  const spacing = effective + margin;

  const { cols, rows } = chooseGrid(count, width, height, spacing);

  // Cellstorlek: aldrig mindre än vad ytan tillåter, aldrig större än behövs.
  const cellW = Math.min(width / cols, Math.max(spacing, width / cols));
  const cellH = Math.min(height / rows, Math.max(spacing, height / rows));

  // Centrera rutnätet i ytan.
  const gridW = cols * cellW;
  const gridH = rows * cellH;
  const offsetX = (width - gridW) / 2;
  const offsetY = (height - gridH) / 2;

  // Håll tärningen innanför spelplanen.
  const half = effective / 2;
  const minX = half;
  const maxX = Math.max(minX, width - half);
  const minY = half;
  const maxY = Math.max(minY, height - half);

  // Slumputrymme inom cellen (aldrig så mycket att grannar kan nås).
  const slackX = Math.max(0, (cellW - effective) / 2) * jitter;
  const slackY = Math.max(0, (cellH - effective) / 2) * jitter;

  const positions: DicePosition[] = [];
  for (let i = 0; i < count; i++) {
    const row = Math.floor(i / cols);
    const col = i % cols;
    // Centrera sista raden om den inte är full.
    const itemsInRow = Math.min(cols, count - row * cols);
    const rowShift = ((cols - itemsInRow) * cellW) / 2;

    const cx = offsetX + rowShift + col * cellW + cellW / 2 + (rng() * 2 - 1) * slackX;
    const cy = offsetY + row * cellH + cellH / 2 + (rng() * 2 - 1) * slackY;

    positions.push({
      x: Math.min(maxX, Math.max(minX, cx)),
      y: Math.min(maxY, Math.max(minY, cy)),
    });
  }
  return positions;
}
